import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Skeleton } from '../../../../ds/components';
import { useSavedPill } from '../../SettingsFrame';
import { organizationKeys, useLegalTexts } from '../../../onboarding/admin/api';
import { LegalEditorDialog, LegalTextList } from '../../../onboarding/admin/LegalParts';
import { LoadError } from '../../../onboarding/admin/LoadError';
import { OrgSettingsPage } from '../brand/OrgSettingsPage';
import styles from './LegalSettingsPage.module.css';

/**
 * Einstellungen 21 · Rechtstexte: the example hint banner (D-333, while a current text is still an example), the four
 * cards with the Pill „Beispieltext", the editor dialog, the footnote.
 */
export function LegalSettingsPage() {
  const { markSaved } = useSavedPill();
  const queryClient = useQueryClient();
  const texts = useLegalTexts();
  const [editing, setEditing] = useState<string | null>(null);
  const item = texts.data?.items.find((entry) => entry.type === editing) ?? null;
  return (
    <OrgSettingsPage
      tabs
      title="Rechtstexte"
      description="DAMOKLES verlinkt diese Texte im Mandantenportal und in Terminbestätigungen. Fertige Texte einfügen oder als PDF hochladen."
    >
      {texts.isPending ? (
        <div className={styles.skeleton}>
          {[0, 1, 2, 3].map((index) => (
            <Skeleton key={index} variant="block" height={78} />
          ))}
        </div>
      ) : texts.isError ? (
        <LoadError
          what="Die Rechtstexte konnten nicht geladen werden"
          error={texts.error}
          retrying={texts.isFetching}
          onRetry={() => void texts.refetch()}
        />
      ) : (
        <>
          {texts.data.banner ? (
            <div className={styles.banner} role="status">
              <div className={styles.bannerText}>
                <span className={styles.bannerTitle}>{texts.data.banner.title}</span>
                <span className={styles.bannerBody}>{texts.data.banner.text}</span>
              </div>
            </div>
          ) : null}
          <div className={styles.list}>
            <LegalTextList texts={texts.data} variant="settings" onEdit={setEditing} onChanged={markSaved} />
          </div>
          <div className={styles.footnote}>{texts.data.footnote}</div>
          <LegalEditorDialog
            item={item}
            maxCharacters={texts.data.maxCharacters}
            onClose={() => setEditing(null)}
            onSaved={(next) => {
              queryClient.setQueryData(organizationKeys.legalTexts, next);
              void queryClient.invalidateQueries({ queryKey: ['organization', 'legal-text'] });
              setEditing(null);
              markSaved();
            }}
          />
        </>
      )}
    </OrgSettingsPage>
  );
}
